/** Existing personal lock operation: one primary account, no new-order risk gate. */
import type {
  ManualLockout,
  ManualLockoutBatch,
  ManualLockoutInventoryResult,
  ManualLockoutTarget,
} from "../gen/helm/v1/risk_pb";
import type { TradingState } from "./state.ts";
import { SESSION_STATUS, type Gate } from "./gate.ts";
import { ALL_ACCOUNTS } from "./accountScope.ts";
export type { ManualLockoutInventoryResult } from "../gen/helm/v1/risk_pb";

function readyStatus(status: number): boolean {
  return status === SESSION_STATUS.READY;
}

export function manualLockoutGate(connected: boolean, state: TradingState, account: string | null): Gate {
  const refuse = (reason: string): Gate => ({ ok: false, code: "NOT_AVAILABLE", reason });
  if (!connected) return refuse("Connect to the gateway first");
  if (!state.session || !readyStatus(state.session.status))
    return refuse("The primary orders session must be READY");
  if (!state.session.manualLockToken) return refuse("This gateway does not support manual lockout requests");
  if (!account || account === ALL_ACCOUNTS)
    return refuse("Choose one account; all-account lockout is not supported");
  const selected = state.accounts.get(account);
  if (!selected) return refuse("The selected account is not active");
  if (selected.loginId !== state.session.loginId)
    return refuse("Manual lockout is supported only on the primary login; select one of its accounts");
  if (!state.risk.has(account)) return refuse("Waiting for the selected account's risk state");
  return { ok: true };
}

export type ManualLockoutPreset =
  | { type: "rest_of_session" | "until_next_session" }
  | { type: "hours"; hours: number }
  | { type: "until"; until_ns: bigint };

export function manualLockoutMessage(
  accountId: string,
  actorToken: string,
  requestId: string,
  preset: ManualLockoutPreset,
  flatten: boolean,
): ManualLockout {
  return {
    $typeName: "helm.v1.ManualLockout",
    accountId,
    actorToken,
    requestId,
    preset: preset.type,
    hours: preset.type === "hours" ? preset.hours : 0,
    untilMs: preset.type === "until" ? preset.until_ns / 1_000_000n : 0n,
    flatten,
  };
}

/** Only gateway-owned READY accounts are candidates; browser financial/risk caches are not inventory. */
export function manualLockoutScope(
  connected: boolean,
  inventory: ManualLockoutInventoryResult | null,
  account: string | null,
  allAccounts: boolean,
): { gate: Gate; targets: ManualLockoutTarget[] } {
  const refuse = (reason: string) => ({
    gate: { ok: false as const, code: "NOT_AVAILABLE" as const, reason },
    targets: [],
  });
  if (!connected) return refuse("Connect to the gateway first");
  if (!inventory?.scopeToken) return refuse("Refresh the current connected-account inventory");
  if (allAccounts && (!inventory.complete || inventory.unavailable.length > 0))
    return refuse(
      "All-account coverage is incomplete. Check unavailable logins and refresh; no batch will be sent.",
    );
  const targets = inventory.targets.filter((t) => allAccounts || t.accountId === account);
  if (targets.length === 0) return refuse("No READY account is available for this scope");
  if (!allAccounts && targets.length !== 1) return refuse("The selected account is ambiguous across logins");
  const identities = new Set(targets.map((t) => JSON.stringify([t.loginId, t.accountId])));
  // The environment-based primary actor has an empty login ID (ADR-0007). Preserve it exactly;
  // the gateway-issued scope/account/actor token remains authoritative for this target.
  if (identities.size !== targets.length || targets.some((t) => !t.accountId || !t.actorToken))
    return refuse("The gateway account inventory is incomplete or ambiguous; refresh it");
  return { gate: { ok: true }, targets };
}

export function manualLockoutBatchMessage(
  inventory: ManualLockoutInventoryResult,
  targets: readonly ManualLockoutTarget[],
  allAccounts: boolean,
  requestId: string,
  preset: ManualLockoutPreset,
  flatten: boolean,
): ManualLockoutBatch {
  const duration = manualLockoutMessage("", "", requestId, preset, flatten);
  return {
    $typeName: "helm.v1.ManualLockoutBatch",
    requestId,
    scopeToken: inventory.scopeToken,
    targets: targets.map((t) => ({ ...t })),
    allAccounts,
    preset: duration.preset,
    hours: duration.hours,
    untilMs: duration.untilMs,
    flatten,
  };
}
